import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import '../styles/App.css';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import TurnstileWidget from '../components/Widget/TurnstileWidget';


const ForgotPassword = ({ onClose, isVisible }) => {
  const { t } = useTranslation(); // Hook to access translation
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileTokenStatus, setTurnstileTokenStatus] = useState(''); // Track Turnstile status
  
  const [emailError, setEmailError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState('');


  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCloseOverlay = () => {
    setEmail('');
    setEmailError('');
    setSubmitError('');
    setSuccess('');
    onClose();

  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSuccess('');

    if (!email.trim()) {
      setEmailError(t('errors.emailRequired'));
      return;
    } else if (!validateEmail(email)) {
      setEmailError(t('errors.invalidEmail'));
      return;
    }

    setLoading(true);
    try {

        // Get user consent for email tracking
        let consentTracking = false;
        if (window.klaro && typeof window.klaro.getManager === 'function') {
            const manager = window.klaro.getManager();
            consentTracking = manager.getConsent('brevo') || false;
        }
        await axios.post(`${baseURL}/api/users/sendNewPassword`, { email, consentTracking });
        setSuccess(t('forgotPassword.success'));
    } catch (error) {
      if (error.response && error.response.status === 403 && error.response.data.isVerified === false) {
        setSubmitError(t('forgotPassword.errors.verifyEmail'));
      
      } else if (error.response && error.response.status === 404) {
        setSubmitError(t('forgotPassword.errors.emailNotRegistered'));
      } else {
        setSubmitError(t('forgotPassword.errors.unexpectedError'));
      }
    } finally {
      setLoading(false);
    }
  };

 // Email validation with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (email && !validateEmail(email)) {
        setEmailError(t('errors.invalidEmail'));
      } else {
        setEmailError('');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [email]);

  if (!isVisible) {
    return null;
  }

  return (
    <Modal show={isVisible} onHide={handleCloseOverlay} centered className="custom-modal">
      <Modal.Header closeButton className="custom-modal-heading">
        <Modal.Title>{t('forgotPassword.pageTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {submitError && (
            <div className="general-error-message">
              <ErrorOutlineIcon className="feedback-icon" />
              {submitError}
            </div>
          )}
          {success && <div className="text-success mb-3">{success}</div>}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('common.emailLabel')}
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setSubmitError('');
            }}
            error={Boolean(emailError)}
            helperText={emailError}
          />

          <TurnstileWidget setTurnstileTokenStatus={setTurnstileTokenStatus} />

          <Button type="submit" 
            variant="contained" 
            disabled={loading || turnstileTokenStatus !== "solved"}
          >
            {loading ? t('common.processingLabel') : t('forgotPassword.resetButton')}
          </Button>
        </Box>
      </Modal.Body>
    </Modal>
  );
};

export default ForgotPassword;